import { schemaResolver, useForm } from '@mantine/form';
import { Group, Select, Stack, TextInput } from '@mantine/core';
import { GENDER_OPTIONS } from '../constants/fieldOptions.ts';
import { LICENSE_CLASS_OPTIONS } from '../constants/licenseClasses.ts';
import { RocDateInput } from './RocDateInput.tsx';
import type {
  ApproveDrivingLicenseInfo,
  DrivingLicenseInfo,
  LicenseClass,
} from '../model.ts';
import { requiredDate, requiredText } from '../utils/formValidation.ts';
import { z } from 'zod';

type DrivingLicenseFormValues = {
  name: string;
  gender: string;
  licenseClass: string;
  nationalId: string;
  birthDate: string | null;
  address: string;
  printNo: string;
  validUntil: string | null;
  issueDate: string | null;
};

type DrivingLicenseFormProps = {
  formId: string;
  initialInfo?: DrivingLicenseInfo;
  readOnly?: boolean;
  onValidSubmit: (info: ApproveDrivingLicenseInfo) => void;
};

const NATIONAL_ID_PATTERN = /^[A-Z](?:[12]\d{8}|[A-D]\d{8}|\d{9})$/;

const READABLE_DISABLED_STYLES = {
  input: {
    color: 'var(--mantine-color-text)',
    opacity: 1,
  },
} as const;

const trimToNull = (value: string) => value.trim() || null;

const buildInitialValues = (
  info?: DrivingLicenseInfo,
): DrivingLicenseFormValues => ({
  name: info?.name ?? '',
  gender: info?.gender ?? '',
  licenseClass: info?.licenseClass ?? '',
  nationalId: info?.nationalId ?? '',
  birthDate: info?.birthDate ?? null,
  address: info?.address ?? '',
  printNo: info?.printNo ?? '',
  validUntil: info?.validUntil ?? null,
  issueDate: info?.issueDate ?? null,
});

const DrivingLicenseFormSchema = z.object({
  name: requiredText('請輸入姓名'),
  gender: requiredText('請選擇性別'),
  licenseClass: requiredText('請選擇駕照種類'),
  nationalId: requiredText('請輸入身分證字號')
    .toUpperCase()
    .regex(NATIONAL_ID_PATTERN, '身分證字號格式不正確'),
  birthDate: requiredDate('請選擇出生年月日'),
  address: requiredText('請輸入地址'),
});

const DrivingLicenseForm = ({
  formId,
  initialInfo,
  readOnly = false,
  onValidSubmit,
}: DrivingLicenseFormProps) => {
  const form = useForm<DrivingLicenseFormValues>({
    initialValues: buildInitialValues(initialInfo),
    validate: schemaResolver(DrivingLicenseFormSchema, { sync: true }),
  });

  const handleSubmit = (values: DrivingLicenseFormValues) => {
    onValidSubmit({
      name: values.name.trim(),
      gender: values.gender as 'MALE' | 'FEMALE',
      licenseClass: values.licenseClass as LicenseClass,
      nationalId: values.nationalId.trim().toUpperCase(),
      birthDate: values.birthDate!,
      address: values.address.trim(),
      printNo: trimToNull(values.printNo),
      validUntil: values.validUntil,
      issueDate: values.issueDate,
    });
  };

  return (
    <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm">
        <Select
          label="駕照種類"
          placeholder="請選擇駕照種類"
          data={LICENSE_CLASS_OPTIONS}
          searchable
          withAsterisk
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('licenseClass')}
        />
        <TextInput
          label="姓名"
          placeholder="請輸入姓名"
          withAsterisk
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('name')}
        />
        <Group grow align="flex-start">
          <Select
            label="性別"
            placeholder="請選擇性別"
            data={GENDER_OPTIONS}
            withAsterisk
            disabled={readOnly}
            styles={READABLE_DISABLED_STYLES}
            {...form.getInputProps('gender')}
          />
          <TextInput
            label="身分證字號"
            placeholder="請輸入身分證字號"
            withAsterisk
            disabled={readOnly}
            styles={READABLE_DISABLED_STYLES}
            {...form.getInputProps('nationalId')}
          />
        </Group>
        <RocDateInput
          label="出生年月日"
          placeholder="請選擇出生年月日"
          withAsterisk
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          defaultDate="1990-01-01"
          defaultLevel="decade"
          {...form.getInputProps('birthDate')}
        />
        <TextInput
          label="地址"
          placeholder="請輸入地址"
          withAsterisk
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('address')}
        />
        <TextInput
          label="印製號碼"
          placeholder="請輸入印製號碼"
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('printNo')}
        />
        <RocDateInput
          label="有效日期"
          placeholder="請選擇有效日期"
          clearable
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('validUntil')}
        />
        <RocDateInput
          label="發照日期"
          placeholder="請選擇發照日期"
          clearable
          disabled={readOnly}
          styles={READABLE_DISABLED_STYLES}
          {...form.getInputProps('issueDate')}
        />
      </Stack>
    </form>
  );
};

export { DrivingLicenseForm };
